import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { subscriptionsAPI, usersAPI } from '../services/api';
import toast from 'react-hot-toast';

const SubscriptionForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    userId: '',
    zoomAccountEmail: '',
    zoomAccountId: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    expiryDate: '',
    durationMonths: 12,
    planType: 'Business',
    purchasePrice: '',
    notes: ''
  });

  useEffect(() => {
    fetchUsers();
    if (isEdit) fetchSubscription();
  }, [id]);

  const fetchUsers = async () => {
    try {
      const res = await usersAPI.getAll();
      setUsers(res.data);
    } catch (error) {
      toast.error('Gagal memuat data user');
    }
  };

  const fetchSubscription = async () => {
    try {
      const res = await subscriptionsAPI.getById(id);
      const sub = res.data;
      setFormData({
        userId: sub.userId,
        zoomAccountEmail: sub.zoomAccountEmail,
        zoomAccountId: sub.zoomAccountId || '',
        purchaseDate: sub.purchaseDate,
        expiryDate: sub.expiryDate,
        durationMonths: sub.durationMonths,
        planType: sub.planType || '',
        purchasePrice: sub.purchasePrice || '',
        notes: sub.notes || ''
      });
    } catch (error) {
      toast.error('Gagal memuat data langganan');
      navigate('/subscriptions');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'purchaseDate' || name === 'durationMonths') {
      const purchaseDate = name === 'purchaseDate' ? value : formData.purchaseDate;
      const duration = name === 'durationMonths' ? parseFloat(value) : parseFloat(formData.durationMonths);
      if (purchaseDate && duration) {
        const expiry = new Date(purchaseDate);
        expiry.setDate(expiry.getDate() + Math.round(duration * 30));
        setFormData(prev => ({ ...prev, expiryDate: expiry.toISOString().split('T')[0] }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEdit) {
        await subscriptionsAPI.update(id, formData);
        toast.success('Berhasil diperbarui');
      } else {
        await subscriptionsAPI.create(formData);
        toast.success('Berhasil ditambahkan');
      }
      navigate('/subscriptions');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Gagal menyimpan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
        {isEdit ? 'Edit Langganan' : 'Tambah Langganan'}
      </h1>

      <form onSubmit={handleSubmit} className="card space-y-4 sm:space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">User *</label>
          <select name="userId" value={formData.userId} onChange={handleChange} className="input-field text-sm" required>
            <option value="">Pilih User</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>{user.name} - {user.department || '-'}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Email Zoom *</label>
          <input type="email" name="zoomAccountEmail" value={formData.zoomAccountEmail} onChange={handleChange}
            className="input-field text-sm" placeholder="contoh@zoom.us" required />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Zoom Account ID</label>
          <input type="text" name="zoomAccountId" value={formData.zoomAccountId} onChange={handleChange}
            className="input-field text-sm" placeholder="ZM-001" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Tanggal Beli *</label>
            <input type="date" name="purchaseDate" value={formData.purchaseDate} onChange={handleChange}
              className="input-field text-sm" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Durasi *</label>
            <select name="durationMonths" value={formData.durationMonths} onChange={handleChange}
              className="input-field text-sm" required>
              <option value="0.03">1 Hari</option>
              <option value="0.14">1 Minggu</option>
              <option value="1">1 Bulan</option>
              <option value="3">3 Bulan</option>
              <option value="6">6 Bulan</option>
              <option value="12">12 Bulan</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Tanggal Kedaluwarsa *</label>
          <input type="date" name="expiryDate" value={formData.expiryDate} onChange={handleChange}
            className="input-field text-sm" required />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Tipe Paket</label>
            <select name="planType" value={formData.planType} onChange={handleChange} className="input-field text-sm">
              <option value="Basic">Basic</option>
              <option value="Pro">Pro</option>
              <option value="Business">Business</option>
              <option value="Enterprise">Enterprise</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Harga (Rp)</label>
            <input type="number" name="purchasePrice" value={formData.purchasePrice} onChange={handleChange}
              className="input-field text-sm" placeholder="0" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Catatan</label>
          <textarea name="notes" value={formData.notes} onChange={handleChange}
            className="input-field text-sm" rows={3} placeholder="Catatan tambahan..." />
        </div>

        <div className="flex gap-3 pt-2 sm:pt-4">
          <button type="button" onClick={() => navigate('/subscriptions')}
            className="btn-secondary flex-1 text-sm sm:text-base">Batal</button>
          <button type="submit" disabled={loading}
            className="btn-primary flex-1 text-sm sm:text-base disabled:opacity-50">
            {loading ? 'Menyimpan...' : isEdit ? 'Simpan' : 'Tambah'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SubscriptionForm;
